/**
 * DrawingToolbar
 *
 * Leaflet-Geoman based drawing controls for creating polygons directly on the map.
 * Emits drawn geometry via onDrawComplete callback.
 * Shows real-time area measurement while drawing.
 */

import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import '@geoman-io/leaflet-geoman-free';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';
import * as turf from '@turf/turf';

interface DrawingToolbarProps {
    map: L.Map | null;
    active: boolean;
    onDrawComplete: (geojson: GeoJSON.Geometry) => void;
    onDrawCancel: () => void;
}

export default function DrawingToolbar({
    map,
    active,
    onDrawComplete,
    onDrawCancel,
}: DrawingToolbarProps) {
    const [measuring, setMeasuring] = useState<string | null>(null);
    const drawnLayerRef = useRef<L.Layer | null>(null);

    useEffect(() => {
        if (!map) return;

        if (active) {
            // Add Geoman controls
            map.pm.addControls({
                position: 'topleft',
                drawMarker: false,
                drawCircleMarker: false,
                drawPolyline: false,
                drawCircle: false,
                drawText: false,
                drawPolygon: true,
                drawRectangle: true,
                editMode: false,
                dragMode: false,
                cutPolygon: false,
                removalMode: false,
                rotateMode: false,
            });

            // Listen for draw create
            const handleCreate = (e: any) => {
                const layer = e.layer;
                drawnLayerRef.current = layer;

                // Extract GeoJSON
                const geojson = layer.toGeoJSON();
                const geometry = geojson.geometry as GeoJSON.Geometry;

                // Calculate area
                const area = turf.area(geojson);
                const areaHa = (area / 10000).toFixed(2);
                setMeasuring(`${areaHa} ha`);

                // Remove Geoman controls after drawing
                map.pm.removeControls();
                map.pm.disableDraw();

                onDrawComplete(geometry);
            };

            map.on('pm:create', handleCreate);

            return () => {
                map.off('pm:create', handleCreate);
                map.pm.removeControls();
                map.pm.disableDraw();
                // Remove drawn layer if exists
                if (drawnLayerRef.current) {
                    map.removeLayer(drawnLayerRef.current);
                    drawnLayerRef.current = null;
                }
                setMeasuring(null);
            };
        } else {
            // Clean up when deactivated
            map.pm.removeControls();
            map.pm.disableDraw();
            if (drawnLayerRef.current) {
                map.removeLayer(drawnLayerRef.current);
                drawnLayerRef.current = null;
            }
            setMeasuring(null);
        }
    }, [map, active, onDrawComplete, onDrawCancel]);

    // Real-time measurement during drawing
    useEffect(() => {
        if (!map || !active) return;

        const handleDrawStart = () => {
            setMeasuring('Dibujando...');
        };

        const handleVertexAdded = (e: any) => {
            try {
                const workingLayer = e.workingLayer;
                if (workingLayer && workingLayer.getLatLngs) {
                    const latlngs = workingLayer.getLatLngs();
                    if (latlngs[0] && latlngs[0].length >= 3) {
                        const coords = latlngs[0].map((ll: L.LatLng) => [ll.lng, ll.lat]);
                        coords.push(coords[0]); // close ring
                        const poly = turf.polygon([coords]);
                        const area = turf.area(poly);
                        setMeasuring(`${(area / 10000).toFixed(2)} ha`);
                    }
                }
            } catch {
                // ignore measurement errors during drawing
            }
        };

        map.on('pm:drawstart', handleDrawStart);
        map.on('pm:vertexadded', handleVertexAdded);

        return () => {
            map.off('pm:drawstart', handleDrawStart);
            map.off('pm:vertexadded', handleVertexAdded);
        };
    }, [map, active]);

    if (!active && !measuring) return null;

    return (
        <>
            {/* Measurement overlay */}
            {measuring && (
                <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[1000] bg-black/80 text-white px-4 py-2 rounded-lg text-sm font-medium shadow-lg">
                    {measuring}
                </div>
            )}

            {/* Cancel button */}
            {active && (
                <div className="absolute top-14 left-3 z-[1000]">
                    <button
                        onClick={() => {
                            if (map) {
                                map.pm.disableDraw();
                                map.pm.removeControls();
                            }
                            if (drawnLayerRef.current && map) {
                                map.removeLayer(drawnLayerRef.current);
                                drawnLayerRef.current = null;
                            }
                            setMeasuring(null);
                            onDrawCancel();
                        }}
                        className="bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium shadow-lg"
                    >
                        Cancelar dibujo
                    </button>
                </div>
            )}
        </>
    );
}
